import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../AuthContext';
import Swal from "sweetalert2";

function Login() {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Force password change states
    const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
    const [tempUserData, setTempUserData] = useState(null);
    const [passwordChangeData, setPasswordChangeData] = useState({ newPassword: '', confirmPassword: '' });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePasswordChangeData = (e) => {
        const { name, value } = e.target;
        setPasswordChangeData({ ...passwordChangeData, [name]: value });
    };

    // ─── Password Strength ────────────────────────────────────────────────────────

    const validatePasswordStrength = (password) => {
        const minLength = 8;
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);
        return {
            isValid: password.length >= minLength && hasLowercase && hasUppercase && hasNumbers && hasSpecialChar,
            requirements: {
                minLength: password.length >= minLength,
                hasLowercase,
                hasUppercase,
                hasNumbers,
                hasSpecialChar
            }
        };
    };

    // ─── Login ────────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(
                'https://apierp.unplannedholiday.in/api/auth/login',
                { email_id: formData.email, password: String(formData.password).trim() },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const data = response.data;
            console.log("Login response:", data);

            // Force password change required
            if (data.user.force_password_change) {
                setTempUserData(data);
                setShowForcePasswordChange(true);
                setLoading(false);
                return;
            }

            // Normal login
            login(
                data.user.id,
                data.token,
                data.refreshToken || '',
                data.user.user_name,
                data.user.email_id,
                data?.companyname || '',
                data?.address1 || '',
                data?.address2 || '',
                data?.city || '',
                data?.zipCode || '',
                data?.phone || ''
            );

            navigate('/');
            setFormData({ email: '', password: '' });

        } catch (err) {
            if (err.response) {
                const msg = err.response.data?.error || err.response.data?.message || 'Login failed';
                setError(msg);
                try {
                    window.Lobibox.notify('error', {
                        pauseDelayOnHover: true,
                        size: 'mini',
                        rounded: true,
                        delayIndicator: false,
                        icon: 'bx bx-x-circle',
                        continueDelayOnInactiveTab: false,
                        position: 'top right',
                        msg
                    });
                } catch (_) {}
            } else {
                setError('An error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ─── Force Password Change ────────────────────────────────────────────────────

    const handleForcePasswordChange = async (e) => {
        e.preventDefault();

        if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
            Swal.fire({ icon: 'error', title: 'Password Mismatch', text: 'New password and confirm password do not match!' });
            return;
        }

        const passwordValidation = validatePasswordStrength(passwordChangeData.newPassword);
        if (!passwordValidation.isValid) {
            let msg = 'Password must meet the following requirements:\n';
            if (!passwordValidation.requirements.minLength) msg += '• At least 8 characters\n';
            if (!passwordValidation.requirements.hasLowercase) msg += '• At least one lowercase letter\n';
            if (!passwordValidation.requirements.hasUppercase) msg += '• At least one uppercase letter\n';
            if (!passwordValidation.requirements.hasNumbers) msg += '• At least one number\n';
            if (!passwordValidation.requirements.hasSpecialChar) msg += '• At least one special character\n';
            Swal.fire({ icon: 'error', title: 'Weak Password', text: msg });
            return;
        }

        try {
            setLoading(true);

            const authHeader = { 'Authorization': `Bearer ${tempUserData.token}`, 'Content-Type': 'application/json' };

            // Step 1: Update password
            await axios.put(
                `https://apierp.unplannedholiday.in/api/users/${tempUserData.user.id}/password`,
                { password: passwordChangeData.newPassword },
                { headers: authHeader }
            );

            // Step 2: Disable force_password_change
            await axios.put(
                `https://apierp.unplannedholiday.in/api/users/${tempUserData.user.id}`,
                { force_password_change: false },
                { headers: authHeader }
            );

            // Step 3: Complete login
            login(
                tempUserData.user.id,
                tempUserData.token,
                tempUserData.refreshToken || '',
                tempUserData.user.user_name,
                tempUserData.user.email_id,
                '', '', '', '', '', ''
            );

            await Swal.fire({
                icon: 'success',
                title: 'Password Changed!',
                text: 'Your password has been updated. Welcome!',
                timer: 2000,
                showConfirmButton: false
            });

            setShowForcePasswordChange(false);
            setTempUserData(null);
            setPasswordChangeData({ newPassword: '', confirmPassword: '' });
            setFormData({ email: '', password: '' });
            navigate('/');

        } catch (err) {
            console.error('Error changing password:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: err.response?.data?.message || 'Failed to change password. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const cancelPasswordChange = () => {
        setShowForcePasswordChange(false);
        setTempUserData(null);
        setPasswordChangeData({ newPassword: '', confirmPassword: '' });
        setError(null);
    };

    const pwdValidation = validatePasswordStrength(passwordChangeData.newPassword);

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            {/* Login Page */}
            <div className="main-wrapper login-body">
                <div className="login-wrapper">
                    <div className="container">
                        <img className="img-fluid logo-dark mb-2 logo-color" src="assets/img/logo2.png" alt="Logo" />
                        <img className="img-fluid logo-light mb-2" src="assets/img/logo2-white.png" alt="Logo" />
                        <div className="loginbox">
                            <div className="login-right">
                                <div className="login-right-wrap">
                                    <h1>Login</h1>
                                    <p className="account-subtitle">Access to our dashboard</p>

                                    <form onSubmit={handleSubmit}>
                                        <div className="input-block mb-3">
                                            <label className="form-control-label">Email Address</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="input-block mb-3">
                                            <label className="form-control-label">Password</label>
                                            <div className="pass-group">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    className="form-control pass-input"
                                                    required
                                                    disabled={loading}
                                                />
                                                <span
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                                                    style={{ cursor: 'pointer' }}
                                                ></span>
                                            </div>
                                        </div>

                                        {error && (
                                            <div className="alert alert-danger mb-3">{error}</div>
                                        )}

                                        <div className="input-block mb-3">
                                            <div className="row">
                                                <div className="col-6">
                                                    <div className="form-check custom-checkbox">
                                                        <input type="checkbox" className="form-check-input" id="cb1" />
                                                        <label className="custom-control-label" htmlFor="cb1">Remember me</label>
                                                    </div>
                                                </div>
                                                <div className="col-6 text-end">
                                                    <a className="forgot-link" href="forgot-password.html">Forgot Password ?</a>
                                                </div>
                                            </div>
                                        </div>

                                        <button className="btn btn-lg btn-primary w-100" type="submit" disabled={loading}>
                                            {loading ? (
                                                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Logging in...</>
                                            ) : 'Login'}
                                        </button>

                                        <div className="login-or">
                                            <span className="or-line"></span>
                                            <span className="span-or">or</span>
                                        </div>

                                        <div className="social-login mb-3">
                                            <span>Login with</span>
                                            <a href="#" className="facebook"><i className="fab fa-facebook-f"></i></a>
                                            <a href="#" className="google"><i className="fab fa-google"></i></a>
                                        </div>

                                        <div className="text-center dont-have">
                                            Don't have an account yet? <a href="register.html">Register</a>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Force Password Change Modal ── */}
            {showForcePasswordChange && (
                <>
                    <div
                        className="modal fade show"
                        tabIndex="-1"
                        style={{ display: 'block', zIndex: 1050 }}
                        data-bs-backdrop="static"
                        data-bs-keyboard="false"
                    >
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content">

                                <div className="modal-header bg-warning text-dark">
                                    <h4 className="modal-title">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        Password Change Required
                                    </h4>
                                </div>

                                <form onSubmit={handleForcePasswordChange}>
                                    <div className="modal-body">

                                        <div className="alert alert-warning">
                                            <i className="fas fa-shield-alt me-2"></i>
                                            <strong>Security Notice:</strong> You must change your password before accessing the system.
                                            This is required for new accounts or after a password reset.
                                        </div>

                                        {/* User Info */}
                                        <div className="bg-light p-3 rounded mb-4">
                                            <h6 className="mb-2">User Information</h6>
                                            <p className="mb-1"><strong>Name:</strong> {tempUserData?.user?.user_name}</p>
                                            <p className="mb-0"><strong>Email:</strong> {tempUserData?.user?.email_id}</p>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="input-block mb-3">
                                                    <label>New Password <span className="text-danger">*</span></label>
                                                    <div className="pass-group">
                                                        <input
                                                            type={showNewPassword ? "text" : "password"}
                                                            className="form-control"
                                                            name="newPassword"
                                                            value={passwordChangeData.newPassword}
                                                            onChange={handlePasswordChangeData}
                                                            placeholder="Enter new password"
                                                            required
                                                        />
                                                        <span
                                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                                            className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                                                            style={{ cursor: 'pointer' }}
                                                        ></span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div className="input-block mb-3">
                                                    <label>Confirm New Password <span className="text-danger">*</span></label>
                                                    <div className="pass-group">
                                                        <input
                                                            type={showConfirmPassword ? "text" : "password"}
                                                            className="form-control"
                                                            name="confirmPassword"
                                                            value={passwordChangeData.confirmPassword}
                                                            onChange={handlePasswordChangeData}
                                                            placeholder="Confirm new password"
                                                            required
                                                        />
                                                        <span
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                                                            style={{ cursor: 'pointer' }}
                                                        ></span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Password Requirements */}
                                            <div className="col-md-12">
                                                <h6>Password Requirements:</h6>
                                                <ul className="list-unstyled">
                                                    {[
                                                        [pwdValidation.requirements.minLength, 'At least 8 characters long'],
                                                        [pwdValidation.requirements.hasLowercase, 'At least one lowercase letter'],
                                                        [pwdValidation.requirements.hasUppercase, 'At least one uppercase letter'],
                                                        [pwdValidation.requirements.hasNumbers, 'At least one number'],
                                                        [pwdValidation.requirements.hasSpecialChar, 'At least one special character'],
                                                        [
                                                            passwordChangeData.newPassword === passwordChangeData.confirmPassword && passwordChangeData.newPassword !== '',
                                                            'Passwords match'
                                                        ]
                                                    ].map(([met, label], i) => (
                                                        <li key={i} className={met ? 'text-success' : 'text-muted'}>
                                                            <i className={`fas ${met ? 'fa-check-circle' : 'fa-circle'} me-2`}></i>
                                                            {label}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={cancelPasswordChange}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={
                                                loading ||
                                                !pwdValidation.isValid ||
                                                passwordChangeData.newPassword !== passwordChangeData.confirmPassword
                                            }
                                        >
                                            {loading ? (
                                                <><span className="spinner-border spinner-border-sm me-2"></span>Updating...</>
                                            ) : (
                                                <><i className="fas fa-key me-2"></i>Change Password & Login</>
                                            )}
                                        </button>
                                    </div>
                                </form>

                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                </>
            )}
        </div>
    );
}

export default Login;